import { useState } from 'react'

interface JobRole {
  id: string
  title: string
  team: string
  location: string
  type: string
  status: 'open' | 'closed'
  description: string
}

const SEED_ROLES: JobRole[] = []

export default function AdminCareers() {
  const [roles, setRoles] = useState<JobRole[]>(SEED_ROLES)
  const [editing, setEditing] = useState<JobRole | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ title: '', team: 'Engineering', location: 'San Jose, CA / Remote', type: 'Full-time', status: 'open' as 'open' | 'closed', description: '' })

  function startNew() {
    setEditing(null)
    setForm({ title: '', team: 'Engineering', location: 'San Jose, CA / Remote', type: 'Full-time', status: 'open', description: '' })
    setShowForm(true)
  }

  function startEdit(role: JobRole) {
    setEditing(role)
    setForm({ title: role.title, team: role.team, location: role.location, type: role.type, status: role.status, description: role.description })
    setShowForm(true)
  }

  function handleSave() {
    if (!form.title.trim()) return
    if (editing) {
      setRoles(rs => rs.map(r => r.id === editing.id ? { ...r, ...form } : r))
    } else {
      setRoles(rs => [...rs, { id: Date.now().toString(), ...form }])
    }
    setShowForm(false)
  }

  function handleDelete(id: string) {
    if (confirm('Remove this role?')) setRoles(rs => rs.filter(r => r.id !== id))
  }

  return (
    <div className="p-8 max-w-4xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-h3 text-chalk">Careers</h1>
          <p className="text-body-sm text-steel mt-1">
            {roles.filter(r => r.status === 'open').length} open roles · manage what appears on <a href="/careers" target="_blank" className="text-signal hover:underline">/careers</a>
          </p>
        </div>
        <button onClick={startNew} className="bg-signal hover:bg-signal-hover text-white text-body-sm font-semibold px-4 py-2 rounded transition-colors">
          + Add Role
        </button>
      </div>

      {/* Empty state */}
      {roles.length === 0 && (
        <div className="bg-obsidian border border-carbon rounded p-12 text-center">
          <div className="text-h2 mb-4">🔭</div>
          <p className="text-title-3 text-chalk font-semibold">No open roles</p>
          <p className="text-body-sm text-steel mt-2 mb-6">The /careers page will show the "no openings" placeholder.</p>
          <button onClick={startNew} className="text-signal text-body-sm font-medium hover:underline">Add a role →</button>
        </div>
      )}

      {/* Roles list */}
      {roles.length > 0 && (
        <div className="bg-obsidian border border-carbon rounded overflow-hidden mb-6">
          <table className="w-full">
            <thead>
              <tr className="border-b border-carbon">
                <th className="text-left text-subhead uppercase tracking-wider text-steel px-4 py-3 font-semibold">Role</th>
                <th className="text-left text-subhead uppercase tracking-wider text-steel px-4 py-3 font-semibold">Team</th>
                <th className="text-left text-subhead uppercase tracking-wider text-steel px-4 py-3 font-semibold">Type</th>
                <th className="text-left text-subhead uppercase tracking-wider text-steel px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {roles.map(role => (
                <tr key={role.id} className="border-b border-carbon/50 hover:bg-carbon/20 transition-colors">
                  <td className="px-4 py-3">
                    <p className="text-body-sm text-chalk font-medium">{role.title}</p>
                    <p className="text-footnote text-steel">{role.location}</p>
                  </td>
                  <td className="px-4 py-3 text-body-sm text-steel">{role.team}</td>
                  <td className="px-4 py-3 text-body-sm text-steel">{role.type}</td>
                  <td className="px-4 py-3">
                    {role.status === 'open'
                      ? <span className="text-caption-1 font-semibold px-2 py-0.5 rounded-full bg-success-dark/15 text-success-dark">Open</span>
                      : <span className="text-caption-1 font-semibold px-2 py-0.5 rounded-full bg-carbon text-steel">Closed</span>
                    }
                  </td>
                  <td className="px-4 py-3 flex gap-3">
                    <button onClick={() => startEdit(role)} className="text-body-sm text-signal hover:text-signal-hover transition-colors">Edit</button>
                    <button onClick={() => handleDelete(role.id)} className="text-body-sm text-error-dark hover:text-error transition-colors">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-obsidian border border-carbon rounded p-5">
        <p className="text-body-sm text-steel">
          <span className="text-chalk font-medium">Note:</span> Roles are stored in-memory for now. To persist them, a database integration is needed. When you're ready to add a role to the live site, update <code className="text-signal font-mono text-footnote">src/pages/Careers.tsx</code> directly.
        </p>
      </div>

      {/* Add/Edit modal */}
      {showForm && (
        <div className="fixed inset-0 bg-void/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-obsidian border border-carbon rounded p-8 max-w-xl w-full">
            <h2 className="text-title-1 text-chalk font-semibold mb-6">{editing ? 'Edit Role' : 'Add Role'}</h2>
            <div className="space-y-4">
              <div>
                <label className="text-body-sm text-steel block mb-1">Job Title</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Senior Computer Vision Engineer" className="w-full bg-void border border-carbon rounded px-4 py-2.5 text-body-sm text-chalk placeholder:text-steel focus:outline-none focus:border-signal" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-body-sm text-steel block mb-1">Team</label>
                  <select value={form.team} onChange={e => setForm(f => ({ ...f, team: e.target.value }))} className="w-full bg-void border border-carbon rounded px-3 py-2.5 text-body-sm text-chalk focus:outline-none focus:border-signal">
                    <option>Engineering</option>
                    <option>Research</option>
                    <option>Product</option>
                    <option>GTM</option>
                    <option>Operations</option>
                  </select>
                </div>
                <div>
                  <label className="text-body-sm text-steel block mb-1">Type</label>
                  <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="w-full bg-void border border-carbon rounded px-3 py-2.5 text-body-sm text-chalk focus:outline-none focus:border-signal">
                    <option>Full-time</option>
                    <option>Part-time</option>
                    <option>Contract</option>
                    <option>Advisor</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-body-sm text-steel block mb-1">Location</label>
                  <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} className="w-full bg-void border border-carbon rounded px-4 py-2.5 text-body-sm text-chalk placeholder:text-steel focus:outline-none focus:border-signal" />
                </div>
                <div>
                  <label className="text-body-sm text-steel block mb-1">Status</label>
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as 'open' | 'closed' }))} className="w-full bg-void border border-carbon rounded px-3 py-2.5 text-body-sm text-chalk focus:outline-none focus:border-signal">
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-body-sm text-steel block mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={4} placeholder="Role description, requirements…" className="w-full bg-void border border-carbon rounded px-4 py-2.5 text-body-sm text-chalk placeholder:text-steel focus:outline-none focus:border-signal resize-none" />
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button onClick={() => setShowForm(false)} className="text-body-sm text-steel hover:text-chalk px-4 py-2 rounded border border-carbon transition-colors">Cancel</button>
              <button onClick={handleSave} className="bg-signal hover:bg-signal-hover text-white text-body-sm font-semibold px-4 py-2 rounded transition-colors">Save Role</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
